import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "../lib/auth";
import { Logo } from "../components/Logo";

export function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen flex items-center justify-center bg-[#FAF7F2] relative overflow-hidden px-4"
    >
      <div className="blob-amber top-0 left-0" />
      <div className="blob-navy bottom-0 right-0" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-md card-soft rounded-3xl p-10 bg-white shadow-xl"
      >
        <div className="flex items-center justify-center mb-6">
          <Logo size="md" />
        </div>
        <div className="w-12 h-12 rounded-2xl bg-[#FFF4EA] flex items-center justify-center mb-5 mx-auto">
          <Lock className="w-5 h-5 text-[#E9610E]" />
        </div>
        <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-[#0F1E3C] text-center mb-2">
          Admin Access
        </h1>
        <p className="text-sm text-[#475569] mb-8 text-center">
          Sign in to view inquiries &amp; applications.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.1em] text-[#64748B] font-semibold block mb-2">Email</span>
            <input
              data-testid="login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white border border-[#EAE3D2] rounded-xl px-4 py-3 text-[#0F1E3C] focus:border-[#E9610E] focus:outline-none focus:ring-2 focus:ring-[#E9610E]/10"
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.1em] text-[#64748B] font-semibold block mb-2">Password</span>
            <input
              data-testid="login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-white border border-[#EAE3D2] rounded-xl px-4 py-3 text-[#0F1E3C] focus:border-[#E9610E] focus:outline-none focus:ring-2 focus:ring-[#E9610E]/10"
            />
          </label>
          <button
            type="submit"
            data-testid="login-submit-btn"
            disabled={loading}
            className="btn-accent w-full"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
